import { confirmInApp } from "./AppDialog";
import React, { useRef, useEffect, useCallback, useState, forwardRef, useImperativeHandle } from "react";
import { ZoomIn, ZoomOut, Trash2, MapPin } from "lucide-react";
import {
  buildInlineImgTagsFromFiles,
  extractImageFilesFromClipboard,
  extractImageFilesFromFileList,
  setImageZoomInHtml,
  removeImageFromHtml,
  ensureImageIds,
  IMAGE_ACCEPT,
} from "../../utils/inlineImages";
import { buildClipboardInsertion } from "../../utils/richPaste";
import { confidenceStyle, confidenceTitle } from "../../utils/confidenceStyle";

function insertHtmlAtCaret(html) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return false;
  const range = sel.getRangeAt(0);
  range.deleteContents();
  range.insertNode(range.createContextualFragment(html));
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
}

function captureEditorRange(root) {
  try {
    const sel = window.getSelection();
    if (!root || !sel || !sel.rangeCount) return null;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return null;
    return range.cloneRange();
  } catch (_e) {
    return null;
  }
}

function restoreEditorRange(range) {
  if (!range) return false;
  try {
    const sel = window.getSelection();
    if (!sel) return false;
    sel.removeAllRanges();
    sel.addRange(range);
    return true;
  } catch (_e) {
    return false;
  }
}

function moveImageToCaret(root, imgId) {
  if (!root) return false;
  const img = root.querySelector(`img[data-img-id="${imgId}"]`);
  if (!img) return false;
  const tag = img.outerHTML;
  img.remove();
  return insertHtmlAtCaret(tag);
}

function tentativeTranscript(tentative) {
  if (!tentative) return "";
  if (typeof tentative === "string") return tentative;
  return tentative.transcript || "";
}

function renderTentativeInto(span, tentative) {
  span.textContent = "";
  const words = !Array.isArray(tentative?.words) ? [] : tentative.words;

  if (!words.length) {
    span.textContent = tentativeTranscript(tentative);
    span.style.cssText = "color:#D4AF37;font-style:italic;opacity:0.82;transition:opacity .18s ease,color .18s ease;";
    return;
  }

  span.style.cssText = "transition:opacity .18s ease,color .18s ease;";

  words.forEach((token, index) => {
    const word = token?.punctuated_word || token?.word || "";
    if (!word) return;

    const wordSpan = document.createElement("span");
    wordSpan.textContent = word;
    wordSpan.title = confidenceTitle(token.confidence);
    Object.assign(wordSpan.style, confidenceStyle(token.confidence));
    span.appendChild(wordSpan);

    if (index < words.length - 1) {
      span.appendChild(document.createTextNode(" "));
    }
  });
}

const ArticleCanvas = forwardRef(function ArticleCanvas({
  html,
  onChange,
  tentative,
  placeholder = "Escribí aquí...",
  commitFlash,
}, ref) {
  const editorRef = useRef(null);
  const skipSyncRef = useRef(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedZoom, setSelectedZoom] = useState(100);

  const readHtml = useCallback(() => {
    const el = editorRef.current;
    if (!el) return "";
    const clone = el.cloneNode(true);
    clone.querySelector(".dictation-tentative")?.remove();
    return ensureImageIds(clone.innerHTML);
  }, []);

  const emitChange = useCallback(() => {
    skipSyncRef.current = true;
    onChange(readHtml());
    requestAnimationFrame(() => { skipSyncRef.current = false; });
  }, [onChange, readHtml]);

  useImperativeHandle(ref, () => ({
    focus: () => editorRef.current?.focus(),
    get innerHTML() {
      return editorRef.current?.innerHTML || "";
    },
    contains: (node) => editorRef.current?.contains(node) || false,
    insertFiles: async (files) => {
      if (!files?.length) return;
      editorRef.current?.focus();
      const tags = await buildInlineImgTagsFromFiles(files);
      if (!insertHtmlAtCaret(tags) && editorRef.current) {
        editorRef.current.insertAdjacentHTML("beforeend", tags);
      }
      emitChange();
    },
  }), [emitChange]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el || skipSyncRef.current) return;
    const clone = document.createElement("div");
    clone.innerHTML = html || "";
    clone.querySelector(".dictation-tentative")?.remove();
    if (el.innerHTML !== clone.innerHTML) {
      el.innerHTML = clone.innerHTML || "";
    }
  }, [html]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    let span = el.querySelector(".dictation-tentative");
    if (!tentative) {
      span?.remove();
      return;
    }
    if (!span) {
      span = document.createElement("span");
      span.className = "dictation-tentative";
      span.contentEditable = "false";
      el.appendChild(span);
    }

    const prefix = el.textContent && !el.textContent.endsWith(" ") ? " " : "";
    renderTentativeInto(span, tentative);
    if (prefix) span.prepend(document.createTextNode(prefix));
  }, [tentative]);

  const handleInput = () => emitChange();

  const handleClick = (e) => {
    const img = e.target.closest("img[data-img-id]");
    if (img) {
      e.preventDefault();
      const id = img.getAttribute("data-img-id");
      setSelectedId(id);
      setSelectedZoom(Number(img.getAttribute("data-zoom")) || 100);
      editorRef.current?.querySelectorAll("img.img-selected").forEach((n) => n.classList.remove("img-selected"));
      img.classList.add("img-selected");
      return;
    }
    setSelectedId(null);
    editorRef.current?.querySelectorAll("img.img-selected").forEach((n) => n.classList.remove("img-selected"));
  };

  const handlePaste = async (e) => {
    const clipboard = e.clipboardData;
    const files = extractImageFilesFromClipboard(clipboard);
    const richHtml = clipboard?.getData("text/html") || "";
    const plainText = clipboard?.getData("text/plain") || "";

    // Plain-text-only paste is best left to the browser; it is the fastest path
    // for very large manuscripts and does not create markup garbage.
    if (!richHtml && !files.length) return;

    e.preventDefault();
    const savedRange = captureEditorRange(editorRef.current);

    try {
      const insertion = await buildClipboardInsertion({
        richHtml,
        plainText,
        imageFiles: files,
      });
      if (!insertion) return;

      editorRef.current?.focus();
      restoreEditorRange(savedRange);
      if (!insertHtmlAtCaret(insertion) && editorRef.current) {
        editorRef.current.insertAdjacentHTML("beforeend", insertion);
      }
      emitChange();
    } catch (error) {
      console.error("[ArticleCanvas] rich paste failed", error);
      // Never turn a failed rich paste into data loss. Fall back to the text.
      if (plainText) {
        editorRef.current?.focus();
        restoreEditorRange(savedRange);
        document.execCommand?.("insertText", false, plainText);
        emitChange();
      }
    }
  };

  const handleDragOver = (e) => {
    const files = extractImageFilesFromFileList(e.dataTransfer?.files);
    if (!files.length) return;
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    const files = extractImageFilesFromFileList(e.dataTransfer?.files);
    if (!files.length) return;
    e.preventDefault();
    editorRef.current?.focus();
    const tags = await buildInlineImgTagsFromFiles(files);
    if (!insertHtmlAtCaret(tags) && editorRef.current) {
      editorRef.current.insertAdjacentHTML("beforeend", tags);
    }
    emitChange();
  };

  const adjustZoom = (delta) => {
    if (!selectedId) return;
    const next = Math.min(200, Math.max(25, selectedZoom + delta));
    setSelectedZoom(next);
    onChange(setImageZoomInHtml(readHtml(), selectedId, next));
  };

  const handleRemove = async () => {
    if (!selectedId) return;
    if (!(await confirmInApp({
      title: "Quitar imagen",
      message: "¿Quitar esta imagen del artículo? Esta confirmación evita borrados accidentales.",
      confirmLabel: "Quitar",
      danger: true,
    }))) return;
    onChange(removeImageFromHtml(readHtml(), selectedId));
    setSelectedId(null);
  };

  const handleMoveHere = async () => {
    if (!selectedId) return;
    if (!(await confirmInApp({
      title: "Mover imagen",
      message: "¿Mover la imagen al cursor actual?",
      confirmLabel: "Mover",
    }))) return;
    moveImageToCaret(editorRef.current, selectedId);
    emitChange();
  };

  const isEmpty = !html?.replace(/<[^>]*>/g, "").trim() && !tentativeTranscript(tentative) && !editorRef.current?.textContent?.trim();

  return (
    <div
      style={{ flex: 1, position: "relative", display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div
        ref={editorRef}
        data-article-canvas
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onClick={handleClick}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "20px 24px",
          color: "#eee",
          fontSize: "1.2rem",
          lineHeight: 1.6,
          outline: "none",
          fontFamily: "inherit",
          minHeight: 0,
          boxShadow: commitFlash ? "inset 0 0 0 2px rgba(74,222,128,0.45)" : undefined,
          transition: "box-shadow 0.25s ease",
        }}
        className={isEmpty ? "article-canvas-empty" : ""}
      />

      {selectedId && (
        <div style={toolbarStyle}>
          <button type="button" onClick={() => adjustZoom(-25)} style={btnStyle} title="Achicar">
            <ZoomOut size={18} />
          </button>
          <span style={{ color: "#D4AF37", fontSize: "0.8rem", minWidth: "44px", textAlign: "center" }}>
            {selectedZoom}%
          </span>
          <button type="button" onClick={() => adjustZoom(25)} style={btnStyle} title="Agrandar">
            <ZoomIn size={18} />
          </button>
          <button type="button" onClick={handleMoveHere} style={btnStyle} title="Mover al cursor">
            <MapPin size={18} />
          </button>
          <button type="button" onClick={handleRemove} style={{ ...btnStyle, color: "#ff6b6b", borderColor: "rgba(255,107,107,0.4)" }} title="Quitar imagen">
            <Trash2 size={18} />
          </button>
        </div>
      )}

      <style>{`
        [data-article-canvas] img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 12px 0;
          display: block;
        }
        [data-article-canvas] img.img-selected {
          outline: 2px solid #D4AF37;
          outline-offset: 3px;
        }
        [data-article-canvas]:empty:before,
        .article-canvas-empty:before {
          content: attr(data-placeholder);
          color: rgba(255,255,255,0.25);
          pointer-events: none;
        }
      `}</style>
    </div>
  );
});

export default ArticleCanvas;

const toolbarStyle = {
  position: "absolute",
  bottom: "16px",
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 12px",
  background: "rgba(20,20,20,0.92)",
  border: "1px solid rgba(212,175,55,0.35)",
  borderRadius: "12px",
  boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
  zIndex: 20,
};

const btnStyle = {
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "8px",
  color: "#fff",
  padding: "8px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
};